import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators } from '@reduxjs/toolkit';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { Button } from '@mui/material';

import GroundTruthEditor from '../components/survey/GroundTruthEditor';
import GTEditorToolbar from '../components/groundtruth/GTEditorToolbar';
import ContentBox from '../components/layout/ContentBox';
import NotificationBox from '../components/layout/NotificationBox';
import { aggregateSubmissions } from '../core/process';
import {
  showNotification,
  showErrorNotification,
  fetchTrialAndSurvey,
  fetchGroundTruths,
  createGroundTruth,
  updateGroundTruth,
} from '../redux/actions';
import { LATEST_GT_VERSION } from '../environment';
import ApiClient from '../loopback/apiClient';
import { RootState, GroundTruth } from '../redux/types';
import withRouter, { RouteType } from '../hoc/withRouter';

type MatchParams = {
  trialId: string;
}

type Props = PropsFromRedux & RouteType<MatchParams>;

type State = {
  hasApprovedCertificates: boolean;
  aggregation?: any;
}

class GroundTruthView extends Component<Props, State> {
  editor: React.RefObject<any>;

  constructor(props: Props) {
    super(props);
    this.editor = React.createRef();
    this.state = {
      hasApprovedCertificates: false,
    };
  }

  componentDidMount() {
    const {
      router,
      fetchTrialAndSurvey: fetchTrialAndSurveyAction,
    } = this.props;
    const trialId = router.params?.trialId;
    fetchTrialAndSurveyAction(Number(trialId));
    ApiClient.hasApprovedCertificate(Number(trialId)).then(({ result }) => {
      this.setState({ hasApprovedCertificates: result });
    });
  }

  componentDidUpdate(prevProps: Props) {
    const { survey: oldSurvey } = prevProps;
    const { survey, fetchGroundTruths: fetchGroundTruthsAction } = this.props;
    if (survey && survey !== oldSurvey) {
      fetchGroundTruthsAction(survey.id);
      ApiClient.getSubmissions(survey.id)
        .then((submissions) => {
          const approvedSubmissions = submissions.filter((submission) => submission.enrollment.status === 'approved');
          this.setState(
            { aggregation: aggregateSubmissions(approvedSubmissions, survey.payload) },
          );
        });
    }
  }

  getSelectedGroundTruth = () => {
    const { groundTruthState } = this.props;
    if (!groundTruthState.selectedGroundTruth) {
      return undefined;
    }
    return groundTruthState.byId[groundTruthState.selectedGroundTruth];
  };

  submitGroundTruth = () => {
    const {
      scorecard,
      minimumScore,
      evaluationFormulas,
      minimumSectionScores,
      minimumScorePerQuestion,
      maximumScorePerQuestion,
      optionalSectionScoring,
      unity,
    } = this.editor.current.getPayload();
    const {
      createGroundTruth: createGroundTruthAction,
      updateGroundTruth: updateGroundTruthAction,
    } = this.props;

    const existingGroundTruth = this.getSelectedGroundTruth();
    const version = (existingGroundTruth && existingGroundTruth.version) || LATEST_GT_VERSION;
    const { survey } = this.props;

    const groundTruthData = {
      lastUpdatedAt: moment.utc(),
      payload: {
        scores: scorecard.json(),
        evaluationFormulas,
        minimumSectionScores,
        optionalSectionScoring,
        minimumScorePerQuestion: version > 5 ? minimumScorePerQuestion : null,
        maximumScorePerQuestion: version > 5 ? maximumScorePerQuestion : null,
        unity: version >= LATEST_GT_VERSION ? unity : null,
      },
      surveyId: survey && survey.id,
      minimumScore,
      version,
    } as Omit<GroundTruth, 'id'>;

    if (existingGroundTruth) {
      updateGroundTruthAction(existingGroundTruth.id, groundTruthData);
    } else {
      createGroundTruthAction(groundTruthData);
    }
  };

  render() {
    const {
      survey,
      trial,
      showNotification: showNotificationAction,
      showErrorNotification: showErrorNotificationAction,
    } = this.props;
    const {
      aggregation,
      hasApprovedCertificates,
    } = this.state;
    const selectedGroundTruth = this.getSelectedGroundTruth();

    if (!survey || !survey.id || !trial) {
      return (
        <NotificationBox>
          <FormattedMessage id="trialmanagement.surveyUnavailable" />
        </NotificationBox>
      );
    }

    return survey && survey.id && (
      <>
        {
          this.state && aggregation
          && (
            <ContentBox title={trial.name}>
              {
                hasApprovedCertificates
                && (
                <NotificationBox>
                  <FormattedMessage id="groundtruthview.unapproveCertificate" />
                </NotificationBox>
                )
              }
              <GTEditorToolbar />
              <GroundTruthEditor
                ref={this.editor}
                survey={survey}
                aggregation={aggregation}
                groundTruth={selectedGroundTruth}
                disabled={trial.deactivate || hasApprovedCertificates}
              />
              <div className="ml2 mt3">
                <Button
                  variant="contained"
                  id="GroundTruthSubmitButton"
                  onClick={async () => {
                    try {
                      await this.submitGroundTruth();
                      await showNotificationAction(<FormattedMessage id="groundtruthview.submittedGroundTruth" />);
                    } catch (error) {
                      showErrorNotificationAction(<FormattedMessage id="genericErrorMessage" />);
                    }
                  }}
                  color="primary"
                  disabled={trial.deactivate || hasApprovedCertificates}
                >
                  <FormattedMessage id="registration.submit" />
                </Button>
              </div>
            </ContentBox>
          )
        }
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  trial: state.trial.trial,
  survey: state.trial.trial ? state.trial.trial.survey : undefined,
  groundTruthState: state.groundTruth,
});

const mapDispatchToProps = (dispatch: any) => (bindActionCreators({
  showNotification,
  showErrorNotification,
  fetchTrialAndSurvey,
  fetchGroundTruths,
  createGroundTruth,
  updateGroundTruth,
}, dispatch));

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(withRouter(GroundTruthView));
